/**
 * Перенос `data/tablet_repository_impl.dart`.
 *
 * Riverpod в порте нет, поэтому `tabletRepositoryProvider` заменён на
 * [tabletRepository] — один общий экземпляр поверх общего `httpClient`.
 * Кому нужен свой транспорт (фоновая цепочка, тесты), конструирует класс
 * сам: `new TabletRepositoryImpl({ client: getBaseClient() })`.
 */

import { Endpoints } from '@/config/endpoints';
import { tabletAuthModelFromJson, type TabletAuthModel } from '@/domain/models/tabletAuthModel';
import {
  tabletConfigModelFromJson,
  type TabletConfigModel,
} from '@/domain/models/tabletConfigModel';
import type {
  TabletAuthParams,
  TabletConfigParams,
  TabletRefreshParams,
  TabletRepository,
  TabletTokens,
} from '@/domain/repository/tabletRepository';
import { AppException, stackTraceOf } from '@/utils/exceptions/appException';
import { httpClient, type HttpClient } from '@/utils/http/httpClient';
import { asRecord } from '@/utils/http/types';

/** Аргументы конструктора [TabletRepositoryImpl]. */
export interface TabletRepositoryImplInit {
  readonly client: HttpClient;
}

export class TabletRepositoryImpl implements TabletRepository {
  constructor({ client }: TabletRepositoryImplInit) {
    this.client = client;
  }

  readonly client: HttpClient;

  async auth({ serverRoot, slug, password }: TabletAuthParams): Promise<TabletAuthModel> {
    try {
      const response = await this.client.post(Endpoints.tabletsAuth(serverRoot), {
        data: {
          slug,
          password,
        },
      });
      return tabletAuthModelFromJson(response.data);
    } catch (e) {
      throw AppException.handle(e, stackTraceOf(e));
    }
  }

  async refresh({ serverRoot, refreshToken }: TabletRefreshParams): Promise<TabletTokens> {
    try {
      const response = await this.client.post(Endpoints.tabletsRefresh(serverRoot), {
        queryParameters: { refresh_token: refreshToken },
      });
      const json = asRecord(response.data);
      // `response.data as Map<String, dynamic>` в Dart бросает `TypeError`,
      // когда тело не объект; слой исключений разбирает его так же.
      if (json === null) throw new TypeError('tablets/refresh: тело ответа не объект');
      const rotated = json['refresh_token'];
      const token = json['token'];
      if (typeof token !== 'string') {
        throw new TypeError('tablets/refresh: поле token не строка');
      }
      return {
        token,
        refreshToken: typeof rotated === 'string' && rotated.length > 0 ? rotated : null,
      };
    } catch (e) {
      throw AppException.handle(e, stackTraceOf(e));
    }
  }

  async fetchTabletConfig({ serverRoot, token }: TabletConfigParams): Promise<TabletConfigModel> {
    try {
      const response = await this.client.get(Endpoints.tabletsConfig(serverRoot), {
        headers: { Authorization: `Bearer ${token}` },
      });
      return tabletConfigModelFromJson(response.data);
    } catch (e) {
      throw AppException.handle(e, stackTraceOf(e));
    }
  }
}

/** Аналог `tabletRepositoryProvider`: репозиторий на общем клиенте. */
export const tabletRepository: TabletRepository = new TabletRepositoryImpl({
  client: httpClient,
});
